import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { resolveBrandingAssetUrl } from '../../core/branding/branding.models';
import { BrandingService } from '../../core/branding/branding.service';
import { ThemeService } from '../../core/services/theme.service';
import { environment } from '../../../environments/environment';

export type BrandLogoVariant = 'auto' | 'green' | 'black' | 'white';

/** Tenant logo values that replace the runtime branding (editor live preview). */
export interface BrandLogoOverride {
  appName: string;
  wordmark: { light: string; dark: string };
  mark: { light: string; dark: string };
}

const WORDMARK_SOURCES: Record<Exclude<BrandLogoVariant, 'auto'>, string> = {
  green: 'assets/logo/logo-green.png',
  black: 'assets/logo/logo-black.png',
  white: 'assets/logo/logo-white.png',
};

const MARK_SOURCES: Record<Exclude<BrandLogoVariant, 'auto'>, string> = {
  green: 'assets/logo/logo-mark-black.png',
  black: 'assets/logo/logo-mark-black.png',
  white: 'assets/logo/logo-mark-white.png',
};

@Component({
  selector: 'dmp-brand-logo',
  standalone: true,
  imports: [CommonModule],
  template: `
    <img
      *ngIf="imageAvailable; else fallback"
      class="brand-logo__image"
      [class.brand-logo__image--wordmark]="showText"
      [class.brand-logo__image--mark]="!showText"
      [src]="logoSrc"
      [alt]="altText"
      [style.height.px]="size"
      [style.width.px]="showText ? null : size"
      [attr.loading]="priority ? 'eager' : 'lazy'"
      [attr.fetchpriority]="priority ? 'high' : 'auto'"
      decoding="async"
      (error)="onImageError()"
    />

    <ng-template #fallback>
      <span class="brand-logo__fallback-wrap">
        <span class="brand-logo__fallback-mark" [style.width.px]="size" [style.height.px]="size">{{ fallbackInitial }}</span>
        <span *ngIf="showText" class="brand-logo__fallback-text">{{ appName }}</span>
      </span>
    </ng-template>
  `,
  styleUrl: './brand-logo.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrandLogoComponent {
  private readonly branding = inject(BrandingService);
  private readonly theme = toSignal(this.themeService.theme$, { initialValue: this.themeService.theme });

  @Input() variant: BrandLogoVariant = 'auto';
  /** Defaults to "<app name> logo". */
  @Input() alt: string | null = null;
  @Input() showText = true;
  @Input() size = 24;
  @Input() priority = false;
  /** Editor preview only: draft logos and name instead of the applied branding. */
  @Input() override: BrandLogoOverride | null = null;
  /** Editor preview only: render for this theme instead of the page theme. */
  @Input() themeOverride: 'light' | 'dark' | null = null;

  private failedSrc: string | null = null;

  constructor(private readonly themeService: ThemeService) {}

  get appName(): string {
    return this.override?.appName || this.branding.appName();
  }

  get altText(): string {
    return this.alt ?? `${this.appName} logo`;
  }

  get fallbackInitial(): string {
    return this.appName.charAt(0).toUpperCase();
  }

  get imageAvailable(): boolean {
    return this.failedSrc !== this.logoSrc;
  }

  get logoSrc(): string {
    const theme = this.themeOverride ?? this.theme();
    const variant = this.variant === 'auto' ? (theme === 'dark' ? 'white' : 'black') : this.variant;
    const slot = variant === 'white' ? 'dark' : 'light';
    const tenant = this.override ?? this.branding.resolved();
    const tenantSrc = (this.showText ? tenant.wordmark : tenant.mark)[slot]?.trim();
    if (tenantSrc) return resolveBrandingAssetUrl(tenantSrc, environment.apiBaseUrl);
    return this.showText ? WORDMARK_SOURCES[variant] : MARK_SOURCES[variant];
  }

  ngOnChanges(): void {
    this.failedSrc = null;
  }

  onImageError(): void {
    this.failedSrc = this.logoSrc;
  }
}
